'use client';

import * as React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardList, Copy, Globe, Loader2, ShieldAlert, TriangleAlert } from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/api/trpc';

type ProvisionResult = inferRouterOutputs<AppRouter>['partners']['provisionDomain'];

/** A DNS row as rendered in a table / serialised into the merchant message. */
interface DnsRow {
  type: string;
  name: string;
  value: string;
  note?: string;
}

/**
 * One-click custom-domain provisioning for a partner.
 *
 * Given the root domain + app sub-domain (the only two things the
 * operator types), this:
 *   1. attaches the app host to the CRM Vercel project, and
 *   2. ensures a Clerk satellite exists for the root domain.
 *
 * The Clerk satellite is billable (~$10/mo) so its creation is gated
 * behind an explicit confirmation modal: the first call runs Vercel +
 * reports whether the satellite is missing; the operator confirms, and
 * a second call creates it. The returned DNS records are rendered for
 * the partner to set at their registrar — and can be copied in one shot
 * (with a ready-to-send merchant message) via "Copy all DNS".
 *
 * All copy is i18n'd (`partnerProvisioning` namespace) so the block
 * follows the operator's CRM language (fr / en) instead of being a
 * hardcoded French island on an English page.
 */
export function DomainProvisioning({
  rootDomain,
  subdomain,
}: {
  rootDomain: string;
  subdomain: string;
}) {
  const { t } = useTranslation('partnerProvisioning');
  const provision = trpc.partners.provisionDomain.useMutation();
  const [result, setResult] = React.useState<ProvisionResult | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const trimmedRoot = rootDomain.trim();
  const trimmedSub = subdomain.trim();
  const canProvision = trimmedRoot.length >= 2 && trimmedSub.length >= 1 && !provision.isPending;

  const run = React.useCallback(
    async (confirmClerkSatellite: boolean) => {
      try {
        const data = await provision.mutateAsync({
          rootDomain: trimmedRoot,
          subdomain: trimmedSub,
          confirmClerkSatellite,
        });
        setResult(data);
        if (data.clerk.needsClerkConfirmation) {
          // Vercel is done; the satellite is missing — ask before billing.
          setConfirmOpen(true);
        } else {
          setConfirmOpen(false);
          toast.success(
            data.clerk.created ? t('toast.provisionedCreated') : t('toast.provisioned'),
          );
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : String(e));
      }
    },
    [provision, trimmedRoot, trimmedSub, t],
  );

  // Flatten Clerk + Vercel records into the list the operator shares
  // with the merchant (no per-record "required/optional/ownership"
  // annotations — those are operator-facing only).
  const allRows: DnsRow[] = React.useMemo(() => {
    if (!result) return [];
    const clerk = result.clerk.records.map((r) => ({ type: r.type, name: r.name, value: r.value }));
    const vercel = result.vercel.records.map((r) => ({ type: r.type, name: r.name, value: r.value }));
    return [...clerk, ...vercel];
  }, [result]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          disabled={!canProvision}
          onClick={() => void run(false)}
        >
          {provision.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Globe className="mr-2 h-4 w-4" />
          )}
          {provision.isPending ? t('button.provisioning') : t('button.provision')}
        </Button>
        <p className="text-xs text-muted-foreground">
          {t('hint', { appHost: `${trimmedSub || '<sub>'}.${trimmedRoot || '<root>'}` })}
        </p>
      </div>

      {result ? (
        <div className="space-y-4 rounded-md border p-3">
          {/* Status line */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={result.clerk.satelliteExists ? 'secondary' : 'outline'}>
              {t('status.clerkSatellite')}&nbsp;·{' '}
              {result.clerk.created
                ? t('status.created')
                : result.clerk.satelliteExists
                  ? t('status.existing')
                  : t('status.missing')}
            </Badge>
            <Badge variant={result.vercel.added ? 'secondary' : 'outline'}>
              {t('status.vercel')}&nbsp;·{' '}
              {result.vercel.alreadyOnProject
                ? t('status.alreadyAttached')
                : result.vercel.added
                  ? t('status.added')
                  : t('status.failed')}
              {result.vercel.verified ? ` · ${t('status.verified')}` : ''}
            </Badge>
          </div>

          {result.clerk.needsClerkConfirmation ? (
            <Alert>
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>{t('needsConfirm.title')}</AlertTitle>
              <AlertDescription>
                {t('needsConfirm.description', { rootDomain: result.rootDomain })}
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Copy-all: one button, ready-to-send merchant message + DNS. */}
          {allRows.length > 0 ? (
            <CopyAllButton
              label={t('copyAll.button')}
              copiedLabel={t('copyAll.copied')}
              failedLabel={t('copyAll.failed')}
              text={buildMerchantMessage(allRows, {
                intro: t('copyAll.intro'),
                outro: t('copyAll.outro'),
              })}
            />
          ) : null}

          {/* Clerk DNS records */}
          {result.clerk.records.length > 0 ? (
            <RecordTable
              title={t('records.clerkTitle')}
              headers={{
                type: t('records.type'),
                name: t('records.name'),
                value: t('records.value'),
              }}
              copyLabel={t('copyRecord')}
              rows={result.clerk.records.map((r) => ({
                type: r.type,
                name: r.name,
                value: r.value,
                note: r.required ? t('records.required') : t('records.optional'),
              }))}
            />
          ) : null}

          {/* Vercel DNS records */}
          <RecordTable
            title={t('records.vercelTitle')}
            headers={{
              type: t('records.type'),
              name: t('records.name'),
              value: t('records.value'),
            }}
            copyLabel={t('copyRecord')}
            rows={result.vercel.records.map((r) => ({
              type: r.type,
              name: r.name,
              value: r.value,
              note: r.note ? t('records.ownership') : undefined,
            }))}
          />
        </div>
      ) : null}

      {/* Billable satellite confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <TriangleAlert className="h-5 w-5 text-amber-500" />
              {t('confirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirm.description', { rootDomain: trimmedRoot })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void run(true)}>
              {t('confirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Build the ready-to-paste message an operator forwards to the merchant.
 * The DNS rows are rendered as a fixed-width-ish block so they survive
 * being pasted into an email / chat.
 */
function buildMerchantMessage(
  rows: DnsRow[],
  copy: { intro: string; outro: string },
): string {
  const typeW = Math.max(4, ...rows.map((r) => r.type.length));
  const nameW = Math.max(4, ...rows.map((r) => r.name.length));
  const lines = rows.map(
    (r) => `${r.type.padEnd(typeW)}  ${r.name.padEnd(nameW)}  →  ${r.value}`,
  );
  return [copy.intro, '', ...lines, '', copy.outro].join('\n');
}

function RecordTable({
  title,
  headers,
  rows,
  copyLabel,
}: {
  title: string;
  headers: { type: string; name: string; value: string };
  rows: DnsRow[];
  copyLabel: string;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-left text-xs">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 font-medium">{headers.type}</th>
              <th className="px-2 py-1.5 font-medium">{headers.name}</th>
              <th className="px-2 py-1.5 font-medium">{headers.value}</th>
              <th className="px-2 py-1.5 font-medium" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.type}-${r.name}-${i}`} className="border-t">
                <td className="px-2 py-1.5 font-mono">{r.type}</td>
                <td className="px-2 py-1.5 font-mono break-all">{r.name}</td>
                <td className="px-2 py-1.5 font-mono break-all">
                  {r.value}
                  {r.note ? (
                    <span className="ml-2 text-[10px] text-muted-foreground">({r.note})</span>
                  ) : null}
                </td>
                <td className="px-2 py-1.5">
                  <CopyButton value={`${r.name} ${r.value}`} label={copyLabel} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Prominent "copy everything (with merchant message)" action. */
function CopyAllButton({
  label,
  copiedLabel,
  failedLabel,
  text,
}: {
  label: string;
  copiedLabel: string;
  failedLabel: string;
  text: string;
}) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {
          toast.error(failedLabel);
        }
      }}
    >
      {copied ? (
        <Check className="mr-2 h-4 w-4 text-emerald-600" />
      ) : (
        <ClipboardList className="mr-2 h-4 w-4" />
      )}
      {copied ? copiedLabel : label}
    </Button>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      className="text-muted-foreground transition-colors hover:text-foreground"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* clipboard denied — silent; the copy-all path surfaces errors */
        }
      }}
      aria-label={label}
      title={label}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}
